import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { captureError, getErrorMessage } from "@/lib/error-message";
import { PIPELINE_STAGE_LABELS, type PipelineStage, effectiveServiceType, fulfillmentTypeFor } from "@/types/quotes";
import { calcQuoteTotals } from "@/lib/quote-totals";

export interface QuoteRow {
  id: string;
  quote_number: string;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  contact_id: string | null;
  // Parent event (#1030). Every new quote gets one; older rows may not.
  event_id?: string | null;
  event_type_id: string | null;
  event_date: string | null;
  event_start_time: string | null;
  event_address: string | null;
  driver_id: string | null;
  service_type: string;
  // Written by the /order RPC and, since #873, the admin form. 'pickup' also means
  // Pickup on pre-#873 /order rows saved as drop_off (see effectiveServiceType).
  fulfillment_type?: string | null;
  guest_count: number;
  status: string;
  pipeline_stage: string;
  revision_requested_at?: string | null;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  discount_amount: number;
  delivery_amount: number;
  service_charge_amount?: number;
  gratuity_percent?: number | null;
  gratuity_amount?: number;
  deposit_percent: number | null;
  total: number;
  notes: string | null;
  // Staff-only (#866): never returned by get_public_quote.
  internal_notes?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  // Provenance (#981): who/what wrote the row when there's no staff user.
  // Stamped by the stamp_record_provenance DB trigger; see src/lib/provenance.ts.
  created_via?: string | null;
  updated_via?: string | null;
  amount_paid: number;
  payment_due_date: string | null;
  // Written by the Square payment function and the payments trigger; the drawer's document label reads it.
  payment_status?: string;
  // Lead source text; the drawer's "Came from".
  source?: string | null;
  // "Waiting on e-transfer" in the quote drawer (#1559). Never counted as paid.
  // Missing from select("*") rows until the #1559 migration is live.
  pending_etransfer_amount?: number | null;
  pending_etransfer_requested_at?: string | null;
  is_lost: boolean;
  square_source?: boolean;
  square_invoice_id?: string | null;
  // First-touch attribution (#505, #506) — nullable, populated by the
  // /order RPC and the wizard/landing-lead quote-creation triggers.
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  fbclid?: string | null;
  gclid?: string | null;
  ghl_synced_at?: string | null;
  event_type?: { id: string; name: string } | null;
  line_items?: QuoteLineItemRow[];
  latest_payment?: { payment_date: string; amount: number; method: string }[] | null;
  created_by_profile?: { display_name: string | null } | null;
  updated_by_profile?: { display_name: string | null } | null;
  // GHL ids of the linked contact (joined), so the "Open in GHL" button can
  // render on the quote without a second fetch (#380).
  // last_message_at added for "last contacted" display (#590).
  contact?: { ghl_contact_id: string | null; ghl_location_id: string | null; last_message_at: string | null } | null;
  // Timestamp when the pipeline stage was last changed (#590).
  pipeline_stage_changed_at?: string | null;
}

export interface QuoteLineItemRow {
  id: string;
  quote_id: string;
  menu_item_id: string | null;
  package_id: string | null;
  name: string;
  description: string | null;
  pricing_type: string;
  unit_price: number;
  quantity: number;
  line_total: number;
  sort_order: number;
}

/**
 * The line_items embed comes back in no guaranteed order. Sort by sort_order
 * so the form, detail panel and duplicate/template copies all see the saved
 * order — the form re-saves sort_order from whatever order it loaded.
 */
export function sortQuoteLineItems<T extends { line_items?: QuoteLineItemRow[] | null }>(quote: T): T {
  if (!quote.line_items?.length) return quote;
  return { ...quote, line_items: [...quote.line_items].sort((a, b) => a.sort_order - b.sort_order) };
}

export function useQuotes() {
  return useQuery({
    queryKey: ["quotes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotes")
        .select("*, event_type:event_types(id, name), line_items:quote_line_items(*), latest_payment:quote_payments(payment_date, amount, method), created_by_profile:profiles!quotes_created_by_fkey(display_name), updated_by_profile:profiles!quotes_updated_by_fkey(display_name), contact:contacts(ghl_contact_id, ghl_location_id, last_message_at)")
        .order("created_at", { ascending: false })
        .order("payment_date", { referencedTable: "quote_payments", ascending: false })
        .limit(1, { referencedTable: "quote_payments" });
      if (error) throw error;
      return (data as QuoteRow[]).map(sortQuoteLineItems);
    },
    // Quotes move through pipeline stages and get status updates from staff
    // often; keep this hook on the pre-#146 behavior so staff returning to
    // the list always see the freshest state rather than a 30s-stale cache.
    refetchOnMount: "always",
  });
}

/**
 * Single quote by id with the same relations as useQuotes(). Useful when the
 * caller knows an id but doesn't have the full list loaded (e.g. opening a
 * quote from the client detail sheet).
 */
export function useQuote(id: string | null | undefined) {
  return useQuery({
    queryKey: ["quotes", "byId", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotes")
        .select("*, event_type:event_types(id, name), line_items:quote_line_items(*), latest_payment:quote_payments(payment_date, amount, method), created_by_profile:profiles!quotes_created_by_fkey(display_name), updated_by_profile:profiles!quotes_updated_by_fkey(display_name), contact:contacts(ghl_contact_id, ghl_location_id, last_message_at)")
        .eq("id", id as string)
        .order("payment_date", { referencedTable: "quote_payments", ascending: false })
        .limit(1, { referencedTable: "quote_payments" })
        .single();
      if (error) throw error;
      return sortQuoteLineItems(data as QuoteRow);
    },
  });
}

export interface CreateQuoteInput {
  client_name: string;
  client_email?: string | null;
  client_phone?: string | null;
  contact_id?: string | null;
  event_type_id?: string | null;
  event_date?: string | null;
  event_start_time?: string | null;
  event_address?: string | null;
  driver_id?: string | null;
  service_type: string;
  fulfillment_type?: string | null;
  guest_count: number;
  notes?: string | null;
  internal_notes?: string | null;
  discount_amount?: number;
  delivery_amount?: number;
  service_charge_amount?: number;
  gratuity_percent?: number | null;
  deposit_percent?: number;
  payment_due_date?: string;
  tax_rate?: number;
  line_items?: Omit<QuoteLineItemRow, "id" | "quote_id">[];
}

// ensure_quote_event (#1030) isn't in the generated types until the next
// types:sync; same shim pattern as useSubmitOrder.ts.
type EnsureQuoteEventRpc = (
  fn: "ensure_quote_event",
  args: { _quote_id: string },
) => Promise<{ data: string | null; error: { message: string } | null }>;

// Creates a saved quote's parent event (#1030). Never throws: the quote
// already exists and an error invites a duplicate re-submit. The RPC is
// idempotent, so a later retry is safe. Returns null after a warning toast
// that starts with `done` ("Quote saved", "Quote duplicated").
async function ensureQuoteEvent(quoteId: string, done: string): Promise<string | null> {
  const rpc = supabase.rpc.bind(supabase) as unknown as EnsureQuoteEventRpc;
  const { data, error } = await rpc("ensure_quote_event", { _quote_id: quoteId });
  if (error) {
    console.error("ensure_quote_event failed", { quoteId, error: error.message });
    const reason = getErrorMessage(error, "");
    const msg = `${done}, but its event record couldn't be created${reason ? `: ${reason}` : "."}`;
    captureError(error, "quote_ensure_event", msg);
    toast.warning(msg);
    return null;
  }
  return data;
}

export function useCreateQuote() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ line_items, ...values }: CreateQuoteInput) => {
      const taxRate = values.tax_rate ?? 13;
      const discount = values.discount_amount ?? 0;
      const delivery = values.delivery_amount ?? 0;
      const serviceCharge = values.service_charge_amount ?? 0;
      const items = line_items ?? [];
      const totals = calcQuoteTotals(items, taxRate, discount, delivery, serviceCharge, values.gratuity_percent ?? 0);

      const { data, error } = await supabase
        .from("quotes")
        .insert({ ...values, quote_number: "", tax_rate: taxRate, deposit_percent: values.deposit_percent ?? null, payment_due_date: values.payment_due_date || null, ...totals, created_by: user?.id, updated_by: user?.id })
        .select()
        .single();
      if (error) throw error;

      // #1030: every quote hangs off an event. authenticated can't insert
      // events (#458), so a staff-only RPC creates the parent event from the
      // saved quote and sets quotes.event_id.
      const eventId =
        (await ensureQuoteEvent(data.id, "Quote saved")) ??
        data.event_id ??
        null;

      if (items.length > 0) {
        const { error: le } = await supabase
          .from("quote_line_items")
          .insert(items.map((li, i) => ({ ...li, quote_id: data.id, sort_order: i })));
        if (le) throw le;
      }
      return { ...data, event_id: eventId };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["events"] });
      toast.success("Quote created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateQuoteStatus() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      manualShare,
      onlyIfUnpaid,
    }: {
      id: string;
      status: string;
      /** Toast shown on success instead of the generic one. */
      successMessage?: string;
      /** draft→sent because staff copied the link to send it themselves: no automatic customer email (#987). */
      manualShare?: boolean;
      /** Accepted → draft (#1559): write only while the row still has nothing paid. */
      onlyIfUnpaid?: boolean;
    }) => {
      if (onlyIfUnpaid) {
        const { data, error } = await supabase
          .from("quotes")
          .update({ status, updated_by: user?.id })
          .eq("id", id)
          .eq("amount_paid", 0)
          .select("id");
        if (error) throw error;
        if (!data?.length) throw new Error("A payment came in on this quote, so it stays accepted.");
        return;
      }
      const { error } =
        manualShare && status === "sent"
          ? await supabase.rpc("mark_quote_sent_without_email", { p_quote_id: id })
          : await supabase.from("quotes").update({ status, updated_by: user?.id }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.success(vars.successMessage ?? "Quote status updated");
    },
    onError: (e: Error, vars) => {
      if (vars.onlyIfUnpaid) qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.error(e.message);
    },
  });
}

// The drawer's Save leaves the event-owned client name and guest count alone
// (Edit details owns them), so an update may omit them (#1559).
export interface UpdateQuoteInput extends Omit<CreateQuoteInput, "client_name" | "guest_count"> {
  id: string;
  client_name?: string;
  guest_count?: number;
}

export function useUpdateQuote() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, line_items, ...values }: UpdateQuoteInput) => {
      const taxRate = values.tax_rate ?? 13;
      const discount = values.discount_amount ?? 0;
      const delivery = values.delivery_amount ?? 0;
      const serviceCharge = values.service_charge_amount ?? 0;
      const items = line_items ?? [];
      const totals = calcQuoteTotals(items, taxRate, discount, delivery, serviceCharge, values.gratuity_percent ?? 0);

      const { error } = await supabase
        .from("quotes")
        .update({ ...values, tax_rate: taxRate, deposit_percent: values.deposit_percent ?? null, payment_due_date: values.payment_due_date || null, ...totals, updated_by: user?.id })
        .eq("id", id);
      if (error) throw error;

      // Delete existing line items and re-insert
      const { error: de } = await supabase
        .from("quote_line_items")
        .delete()
        .eq("quote_id", id);
      if (de) throw de;

      if (items.length > 0) {
        const { error: le } = await supabase
          .from("quote_line_items")
          .insert(items.map((li, i) => ({ ...li, quote_id: id, sort_order: i })));
        if (le) throw le;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.success("Quote updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
export function useUpdatePipelineStage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, pipeline_stage }: { id: string; pipeline_stage: string }) => {
      const { error } = await supabase.from("quotes").update({ pipeline_stage, updated_by: user?.id }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      // Tap-to-move (the card overflow menu) has no other feedback that the
      // move happened — this matters most for "Won", which isn't a visible
      // board column, so without a toast the card just silently disappears.
      const label = PIPELINE_STAGE_LABELS[vars.pipeline_stage as PipelineStage] ?? vars.pipeline_stage;
      toast.success(`Moved to ${label}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useToggleLost() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, is_lost }: { id: string; is_lost: boolean }) => {
      const { error } = await supabase.from("quotes").update({ is_lost, updated_by: user?.id }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.success(vars.is_lost ? "Marked as lost" : "Re-opened");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("quotes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event-detail"] });
      toast.success("Quote deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDuplicateQuote() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (sourceQuote: QuoteRow) => {
      const serviceType = effectiveServiceType(sourceQuote.service_type, sourceQuote.fulfillment_type);
      const { data, error } = await supabase
        .from("quotes")
        .insert({
          quote_number: "",
          client_name: sourceQuote.client_name,
          client_email: sourceQuote.client_email,
          client_phone: sourceQuote.client_phone,
          event_type_id: sourceQuote.event_type_id,
          event_date: sourceQuote.event_date,
          service_type: serviceType,
          fulfillment_type: fulfillmentTypeFor(serviceType),
          guest_count: sourceQuote.guest_count,
          status: "draft",
          subtotal: sourceQuote.subtotal,
          tax_rate: sourceQuote.tax_rate,
          tax_amount: sourceQuote.tax_amount,
          discount_amount: sourceQuote.discount_amount,
          delivery_amount: sourceQuote.delivery_amount ?? 0,
          service_charge_amount: sourceQuote.service_charge_amount ?? 0,
          gratuity_percent: sourceQuote.gratuity_percent ?? null,
          gratuity_amount: sourceQuote.gratuity_amount ?? 0,
          total: sourceQuote.total,
          notes: sourceQuote.notes,
          internal_notes: sourceQuote.internal_notes ?? null,
          payment_due_date: sourceQuote.payment_due_date,
          // #1039: a duplicate is another option for the same booking, so it
          // shares the original's event.
          event_id: sourceQuote.event_id ?? null,
          created_by: user?.id,
          updated_by: user?.id,
        })
        .select()
        .single();
      if (error) throw error;

      // An original from before #1030 has no event: give the copy its own, as
      // useCreateQuote does.
      const eventId =
        data.event_id ??
        (await ensureQuoteEvent(data.id, "Quote duplicated"));

      if (sourceQuote.line_items?.length) {
        const { error: le } = await supabase
          .from("quote_line_items")
          .insert(
            sourceQuote.line_items.map((li, i) => ({
              quote_id: data.id,
              menu_item_id: li.menu_item_id,
              package_id: li.package_id,
              name: li.name,
              description: li.description,
              pricing_type: li.pricing_type,
              unit_price: li.unit_price,
              quantity: li.quantity,
              line_total: li.line_total,
              sort_order: i,
            }))
          );
        if (le) throw le;
      }
      return { ...data, event_id: eventId };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["events"] });
      toast.success("Quote duplicated as new draft");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
